import {yupResolver} from '@hookform/resolvers/yup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  ListItemText,
  MenuItem,
  Radio,
  Typography,
} from '@mui/material';
import {SxProps, Theme} from '@mui/material/styles';
import MuiTextField from '@mui/material/TextField';
import {
  ApplicationTemplate,
  ApplicationType,
  ApplicationTypeAvailableOptions,
  Collections,
  copyWithoutRef,
  DiscountDuration,
  EquipmentViewableBy,
  Firebase,
  getEquipmentSchemaWithoutId,
  getWhenAppTypeSchema,
  isPropsRequired,
  PageFields,
  PCIFee,
  PCIFeeDefaultValues,
  PCIFeeTypes,
  PCIFeeWithoutWaived,
  ProcessingTypePayments,
  ProcessingTypes,
  StatementType,
  useAgentsFromGroup,
  useNotification,
  useRatePrograms,
} from '@ozark/common';
import {
  ErrorBoundary,
  ProgramEquipment,
  RadioGroup,
  SectionTitle,
  SectionTitleSwitch,
  Select,
  TextField,
} from '@ozark/common/components';
import {PRICING_VISA_MASTER} from '@ozark/common/constants/strings';
import {getAdjustedDiscountRateBilledToMerchant, resetProgramFields} from '@ozark/common/helpers';
import {useViewableEquipment} from '@ozark/common/hooks/useEquipment';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {useStore} from '../../store/helpers';

const schema = yup.object().shape({
  templateName: yup.string().required(),
  equipment: getEquipmentSchemaWithoutId({useCustomErrorMessage: true}),
  processingType: yup
    .string()
    .required('You must select an option')
    .oneOf(Object.values(ProcessingTypes)),
  rateSet: yup
    .object()
    .required()
    .when('processingType', (processingType: ProcessingTypes) => {
      return yup.object().shape(getWhenAppTypeSchema(true, processingType));
    }),
});

type AgentInfo = {
  id: string;
  firstName: string;
  lastName: string;
};

const ALL_AGENTS_ID = 'ALL';

export const styles: Record<string, SxProps<Theme>> = {
  icon: {
    verticalAlign: 'bottom',
    height: 20,
    width: 20,
  },
  title: {
    marginLeft: theme => theme.spacing(2),
    marginBottom: theme => theme.spacing(-1.5),
    fontSize: '1.2em',
    sm: {
      fontSize: '1em',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'row',
    flexBasis: '20%',
    alignItems: 'center',
  },
  toggle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: theme => theme.spacing(2),
  },
  grow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: theme => theme.spacing(2),
    flexGrow: 1,
    '& > *': {
      margin: theme => theme.spacing(0, 0.5),
    },
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  processingTypeTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    align: 'left',
    alignItems: 'center',
    paddingLeft: theme => theme.spacing(1),
    '& > *': {
      marginRight: theme => theme.spacing(2),
    },
  },
};

export const PricingTemplate = ({
  template,
  whichExpanded,
  onChange,
  onSubmit,
}: {
  template: ApplicationTemplate;
  whichExpanded: string | false;
  onChange: (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onSubmit: () => void;
}) => {
  const {authUser, claims, group} = useStore();
  const [saving, setSaving] = useState(false);
  const showNotification = useNotification();

  const {
    documents: {data: agents},
  } = useAgentsFromGroup(authUser.data?.uid, claims?.groupId, claims?.groupRole);

  const [otherVolumeAndItemFeeSection, setOtherVolumeAndItemFeeSection] = useState(
    template.rateSet?.otherVolumeRate !== 0 || template.rateSet?.otherItemFee !== 0
  );

  const [shareSection, setShareSection] = useState(template.shareWithAgentsEnabled ?? false);

  const formMethods = useForm<ApplicationTemplate>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...copyWithoutRef(template),
    },
    shouldUnregister: true,
    shouldFocusError: true,
  });

  const {
    formState: {errors, isDirty},
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
  } = formMethods;

  const readonly = template.createdBy !== authUser.data?.uid;

  const isOnlineApplicationPricingTypesOverrideEnabled =
    group.data?.applicationSettings?.disableOnlineApplicationPricingTypes;
  const disabledApplicationPricingTypesDisabled =
    group.data?.applicationSettings?.disabledOnlineApplicationPricingTypes ?? [];

  const applicationOptions = isOnlineApplicationPricingTypesOverrideEnabled
    ? ApplicationTypeAvailableOptions.filter(
        x =>
          !disabledApplicationPricingTypesDisabled.includes(
            Object.keys(ApplicationType)[Object.values(ApplicationType).indexOf(x)]
          )
      )
    : ApplicationTypeAvailableOptions;

  // Add ALL_AGENTS_ID to agents list
  // And remove current user
  const agentsList = React.useMemo(
    () => [
      {
        id: ALL_AGENTS_ID,
        firstName: 'ALL',
        lastName: 'AGENTS',
      },
      ...(agents
        ?.filter(x => x.id !== authUser.data?.uid)
        .map(x => ({id: x.id, firstName: x.firstName, lastName: x.lastName})) ?? []),
    ],
    [agents, authUser.data?.uid]
  );

  const watchProcessingType = watch('processingType');
  const shareWithAgents = watch('shareWithAgents') ?? template.shareWithAgents;

  const {equipment} = useViewableEquipment(
    watchProcessingType || ProcessingTypes.cardPresent,
    EquipmentViewableBy.agent
  );

  const {ratePrograms} = useRatePrograms();

  const equipments = useMemo(() => {
    if (group.promised || !group.data || equipment.promised || !equipment.data) {
      return [];
    }

    const disabledEquipmentsSet = new Set(group.data.disabledEquipments ?? []);
    return equipment.data.filter(e => !disabledEquipmentsSet.has(e.id));
  }, [equipment, group]);

  const watchApplicationType: ApplicationType = watch('rateSet.applicationType');
  const watchQualifiedDiscountRateVisaMastercardDiscover = watch<string>(
    'rateSet.qualifiedDiscountRateVisaMastercardDiscover'
  );
  const watchQualifiedDebitPINRate = watch<string>('rateSet.qualifiedDebitPINRate');
  const watchQualifiedTransactionFeeVisaMastercardDiscover = watch(
    'rateSet.qualifiedTransactionFeeVisaMastercardDiscover'
  );
  const pciFeeTypes = Object.keys(PCIFeeTypes);
  const watchPCIFee = watch('rateSet.pciFee', PCIFee.waived);
  const displayPCIFeeValue = PCIFeeWithoutWaived.includes(watchPCIFee as PCIFee);
  const isCnpVCp = watchApplicationType === ApplicationType.cnpVCp;
  const isErr = watchApplicationType === ApplicationType.err;
  const showDebitRates = !isCnpVCp || watchProcessingType === ProcessingTypes.cardPresent;

  const isDebit = ![
    ApplicationType.interchange,
    ApplicationType.interchangeEBTMonthly,
    ApplicationType.err,
    ApplicationType.flatRate,
    ApplicationType.tiered,
  ].includes(watchApplicationType);

  const autoFillMidAndNonQual =
    watchApplicationType &&
    [
      ApplicationType.surcharge,
      ApplicationType.dualPricingPassThroughFees,
      ApplicationType.dualPricingPassThroughFeesEBTMonthly,
      ApplicationType.dualPricingFlatRate,
      ApplicationType.dualPricingFlatRateEBTMonthly,
      ApplicationType.cashDiscount,
      ApplicationType.flatRate,
      ApplicationType.interchange,
      ApplicationType.interchangeEBTMonthly,
    ].includes(watchApplicationType);

  useEffect(() => {
    if (
      watchApplicationType !== ApplicationType.cashDiscount &&
      watchApplicationType !== ApplicationType.surcharge &&
      watchApplicationType !== ApplicationType.dualPricingPassThroughFees &&
      watchApplicationType !== ApplicationType.dualPricingPassThroughFeesEBTMonthly &&
      watchApplicationType !== ApplicationType.dualPricingFlatRate &&
      watchApplicationType !== ApplicationType.dualPricingFlatRateEBTMonthly
    )
      return;
    setValue('rateSet.qualifiedDiscountRateAmex', watchQualifiedDiscountRateVisaMastercardDiscover);
    setValue(
      'rateSet.qualifiedTransactionFeeAmex',
      watchQualifiedTransactionFeeVisaMastercardDiscover
    );
  }, [
    setValue,
    watchApplicationType,
    watchQualifiedDiscountRateVisaMastercardDiscover,
    watchQualifiedTransactionFeeVisaMastercardDiscover,
  ]);

  const handlePCIFeeChange = (value: string) => {
    setValue('rateSet.pciFeeValue', PCIFeeDefaultValues[value as PCIFee]);
  };

  useEffect(() => {
    resetProgramFields(setValue, watchApplicationType, watchProcessingType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchApplicationType, watchProcessingType, setValue]);

  const handleResetClick = () => {
    reset();
  };

  const onSuccess = async (data: any) => {
    setSaving(true);
    await Firebase.firestore
      .collection(Collections.applicationsTemplates)
      .doc(template.id)
      .set(data, {merge: true});
    reset(data);
    setSaving(false);
    onSubmit();
    showNotification('success', 'Pricing template successfully updated.');
  };

  const onError = (_err: any) => {
    console.log(_err);
    showNotification('error', 'An error occurred while saving. Please fill all required fields');
  };

  const getAgentLabel = (agent: AgentInfo) => `${agent.firstName} ${agent.lastName}`;

  const onSelectAgentsChange = (_event: React.ChangeEvent<{}>, selectedAgents: AgentInfo[]) => {
    if (selectedAgents.find(x => x.id === ALL_AGENTS_ID)) {
      setValue(
        'shareWithAgents',
        agentsList
          .filter(x => x.id !== ALL_AGENTS_ID)
          .map(x => ({
            id: x.id,
            fullName: `${x.firstName} ${x.lastName}`,
          })),
        {shouldDirty: true}
      );
      setValue(
        'shareWithAgentsIds',
        agentsList.filter(x => x.id !== ALL_AGENTS_ID).map(x => x.id),
        {shouldDirty: true}
      );
      return;
    }

    setValue(
      'shareWithAgents',
      selectedAgents.map(x => ({
        id: x.id,
        fullName: `${x.firstName} ${x.lastName}`,
      })),
      {shouldDirty: true}
    );
    setValue(
      'shareWithAgentsIds',
      selectedAgents.map(x => x.id),
      {shouldDirty: true}
    );
  };

  const displayProcessingType = (processingType: ProcessingTypes) => {
    const paymentInfo = ProcessingTypePayments[processingType];

    return (
      <FormControlLabel
        key={processingType}
        value={processingType}
        control={<Radio />}
        label={
          <Typography>
            {paymentInfo.title} &nbsp;
            <Typography variant="caption">&bull; {paymentInfo.caption}</Typography>
          </Typography>
        }
      />
    );
  };

  return (
    <FormProvider {...formMethods}>
      <Accordion
        key={template.id}
        TransitionProps={{unmountOnExit: true}}
        expanded={whichExpanded === template.templateName}
        onChange={onChange(template.templateName)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel-${template.id}-content`}
          id={`panel-${template.id}-header`}
        >
          <Box sx={styles.toggle}>
            {/* <FiberManualRecordSharpIcon
              color={!rateProgram.global && rateProgram.active ? 'primary' : 'disabled'}
            /> */}
          </Box>
          <Box sx={styles.column}>
            <Typography variant="body1" color={readonly ? 'secondary' : 'primary'}>
              {template.templateName}
              {readonly ? ' (shared)' : ''}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                name="templateName"
                label="Template Name"
                disabled={readonly}
                errors={errors}
                control={control}
              />
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                name="processingType"
                label="How will the merchant be processing payments?"
                defaultValue=""
                required
                errors={errors}
                control={control}
                disabled={readonly}
              >
                {Object.keys(ProcessingTypePayments).map(key =>
                  displayProcessingType(key as ProcessingTypes)
                )}
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <Select
                name="rateSet.applicationType"
                label="Application Type"
                required
                disabled={readonly}
                errors={errors}
                control={control}
                options={applicationOptions}
              />
            </Grid>
            <Grid item xs={12}>
              <Select
                name="rateSet.discountDuration"
                label="Discount Duration"
                required
                disabled={readonly}
                defaultValue={DiscountDuration.daily}
                errors={errors}
                control={control}
                options={Object.values(DiscountDuration)}
              />
            </Grid>
            <Grid item xs={12}>
              <Select
                name="rateSet.mailStatement"
                label="Mail Statement"
                required
                disabled={readonly}
                errors={errors}
                control={control}
                yesOrNo
              />
            </Grid>
            {(watchApplicationType === ApplicationType.interchange ||
              watchApplicationType === ApplicationType.interchangeEBTMonthly) && (
              <Grid item xs={12}>
                <Select
                  name="rateSet.statementType"
                  label="Statement Type"
                  defaultValue={StatementType.summary}
                  required
                  disabled={readonly}
                  errors={errors}
                  control={control}
                  options={Object.values(StatementType)}
                  compareFn={() => 0}
                />
              </Grid>
            )}
            {watchApplicationType === ApplicationType.surcharge && (
              <Fragment>
                <Grid item xs={12}>
                  <SectionTitle text="Visa, Mastercard, Discover and AMEX" />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="rateSet.qualifiedDiscountRateVisaMastercardDiscover"
                    label={'Credit Rate'}
                    required
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    helperText={
                      watchQualifiedDiscountRateVisaMastercardDiscover &&
                      `Adjusted Discount Rate Billed to Merchant ${getAdjustedDiscountRateBilledToMerchant(
                        watchQualifiedDiscountRateVisaMastercardDiscover
                      )}`
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="rateSet.qualifiedTransactionFeeVisaMastercardDiscover"
                    label="Transaction Fee"
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Fragment>
            )}
            {(watchApplicationType === ApplicationType.cashDiscount ||
              watchApplicationType === ApplicationType.dualPricingPassThroughFees ||
              watchApplicationType === ApplicationType.dualPricingPassThroughFeesEBTMonthly ||
              watchApplicationType === ApplicationType.dualPricingFlatRate ||
              watchApplicationType === ApplicationType.dualPricingFlatRateEBTMonthly) && (
              <Fragment>
                <Grid item xs={12}>
                  <SectionTitle text="Visa, Mastercard, Discover and AMEX" />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="rateSet.qualifiedDiscountRateVisaMastercardDiscover"
                    label={
                      watchApplicationType === ApplicationType.dualPricingPassThroughFees ||
                      watchApplicationType ===
                        ApplicationType.dualPricingPassThroughFeesEBTMonthly ||
                      watchApplicationType === ApplicationType.dualPricingFlatRate ||
                      watchApplicationType === ApplicationType.dualPricingFlatRateEBTMonthly
                        ? 'Credit/Signature Debit Rate'
                        : 'Credit/Debit Rate'
                    }
                    required
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    helperText={
                      watchQualifiedDiscountRateVisaMastercardDiscover &&
                      `Adjusted Discount Rate Billed to Merchant ${getAdjustedDiscountRateBilledToMerchant(
                        watchQualifiedDiscountRateVisaMastercardDiscover
                      )}`
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="rateSet.qualifiedTransactionFeeVisaMastercardDiscover"
                    label="Transaction Fee"
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </Fragment>
            )}
            {(watchApplicationType === ApplicationType.dualPricingPassThroughFees ||
              watchApplicationType === ApplicationType.dualPricingPassThroughFeesEBTMonthly ||
              watchApplicationType === ApplicationType.dualPricingFlatRate ||
              watchApplicationType === ApplicationType.dualPricingFlatRateEBTMonthly) && (
              <>
                {watchProcessingType === ProcessingTypes.cardPresent && (
                  <>
                    <Grid item xs={12}>
                      <SectionTitle text="Debit" />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="rateSet.qualifiedDebitPINRate"
                        label="Debit Pin Based Rate"
                        required
                        type="number"
                        disabled={readonly}
                        errors={errors}
                        control={control}
                        defaultValue="0"
                        helperText={
                          (watchApplicationType === ApplicationType.dualPricingFlatRate ||
                            watchApplicationType ===
                              ApplicationType.dualPricingFlatRateEBTMonthly) &&
                          watchQualifiedDebitPINRate &&
                          `Adjusted Discount Rate Billed to Merchant ${getAdjustedDiscountRateBilledToMerchant(
                            watchQualifiedDebitPINRate
                          )}`
                        }
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="rateSet.qualifiedDebitPINFee"
                        label="Debit Pin Based Transaction Fee"
                        required
                        disabled={readonly}
                        type="number"
                        errors={errors}
                        control={control}
                        defaultValue="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <SectionTitle text="EBT" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="rateSet.ebtDiscountRate"
                    label="EBT Discount Rate"
                    required
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    defaultValue="0"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="rateSet.ebtTransactionFee"
                    label="EBT Transaction Fee"
                    required
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    defaultValue="0"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </>
            )}

            {watchApplicationType === ApplicationType.surcharge && (
              <>
                <>
                  <Grid item xs={12}>
                    <SectionTitle text="Debit" />
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <TextField
                      name="rateSet.qualifiedDebitNonPINRate"
                      label="Signature Debit Rate"
                      required
                      type="number"
                      disabled={readonly}
                      errors={errors}
                      control={control}
                      defaultValue="0"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>

                  {watchProcessingType === ProcessingTypes.cardPresent && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="rateSet.qualifiedDebitPINRate"
                          label="Debit Pin Based Rate"
                          required
                          type="number"
                          disabled={readonly}
                          errors={errors}
                          control={control}
                          defaultValue="0"
                          helperText={
                            watchQualifiedDebitPINRate &&
                            `Adjusted Discount Rate Billed to Merchant ${getAdjustedDiscountRateBilledToMerchant(
                              watchQualifiedDebitPINRate
                            )}`
                          }
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="rateSet.qualifiedDebitPINFee"
                          label="Debit Pin Based Transaction Fee"
                          required
                          type="number"
                          disabled={readonly}
                          errors={errors}
                          control={control}
                          defaultValue="0"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </>

                <Grid item xs={12}>
                  <SectionTitle text="EBT" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="rateSet.ebtDiscountRate"
                    label="EBT Discount Rate"
                    required
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    defaultValue="0"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="rateSet.ebtTransactionFee"
                    label="EBT Transaction Fee"
                    required
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    defaultValue="0"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </>
            )}
            {watchApplicationType !== ApplicationType.cashDiscount &&
              watchApplicationType !== ApplicationType.surcharge &&
              watchApplicationType !== ApplicationType.dualPricingPassThroughFees &&
              watchApplicationType !== ApplicationType.dualPricingPassThroughFeesEBTMonthly &&
              watchApplicationType !== ApplicationType.dualPricingFlatRate &&
              watchApplicationType !== ApplicationType.dualPricingFlatRateEBTMonthly && (
                <Fragment>
                  <Grid item xs={12}>
                    <SectionTitle text={`${PRICING_VISA_MASTER}${isDebit ? ' and Debit' : ''}`} />
                  </Grid>
                  <Grid item xs={12} sm={autoFillMidAndNonQual || isCnpVCp || isErr ? 6 : 4}>
                    <TextField
                      name="rateSet.qualifiedDiscountRateVisaMastercardDiscover"
                      label="Qualified Rate"
                      required={isPropsRequired(
                        PageFields.qualifiedDiscountRateVisaMastercardDiscover,
                        watchApplicationType
                      )}
                      type="number"
                      disabled={readonly}
                      errors={errors}
                      control={control}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>
                  {!autoFillMidAndNonQual && (
                    <Fragment>
                      {!isCnpVCp && !isErr && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name="rateSet.midQualifiedDiscountRateVisaMastercardDiscover"
                            label="Mid-Qualified Rate"
                            required={isPropsRequired(
                              PageFields.midQualifiedDiscountRateVisaMastercardDiscover,
                              watchApplicationType
                            )}
                            type="number"
                            disabled={readonly}
                            errors={errors}
                            control={control}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={isCnpVCp || isErr ? 6 : 4}>
                        <TextField
                          name="rateSet.nonQualifiedDiscountRateVisaMastercardDiscover"
                          label={isErr ? 'Non-Qualified Surcharge' : 'Non-Qualified Rate'}
                          required={isPropsRequired(
                            PageFields.nonQualifiedDiscountRateVisaMastercardDiscover,
                            watchApplicationType
                          )}
                          type="number"
                          disabled={readonly}
                          errors={errors}
                          control={control}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Fragment>
                  )}
                  <Grid item xs={12} sm={autoFillMidAndNonQual || isCnpVCp || isErr ? 6 : 4}>
                    <TextField
                      name="rateSet.qualifiedTransactionFeeVisaMastercardDiscover"
                      label="Qualified Transaction Fee"
                      required={isPropsRequired(
                        PageFields.qualifiedTransactionFeeVisaMastercardDiscover,
                        watchApplicationType
                      )}
                      type="number"
                      disabled={readonly}
                      errors={errors}
                      control={control}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                  {!autoFillMidAndNonQual && (
                    <Fragment>
                      {!isCnpVCp && !isErr && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name="rateSet.midQualifiedTransactionFeeVisaMastercardDiscover"
                            label="Mid-Qualified Transaction Fee"
                            required={isPropsRequired(
                              PageFields.midQualifiedTransactionFeeVisaMastercardDiscover,
                              watchApplicationType
                            )}
                            disabled={readonly}
                            type="number"
                            errors={errors}
                            control={control}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </Grid>
                      )}
                      {!isErr && (
                        <Grid item xs={12} sm={isCnpVCp ? 6 : 4}>
                          <TextField
                            name="rateSet.nonQualifiedTransactionFeeVisaMastercardDiscover"
                            label="Non-Qualified Transaction Fee"
                            required={isPropsRequired(
                              PageFields.nonQualifiedTransactionFeeVisaMastercardDiscover,
                              watchApplicationType
                            )}
                            type="number"
                            disabled={readonly}
                            errors={errors}
                            control={control}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </Grid>
                      )}
                    </Fragment>
                  )}

                  <Grid item xs={12}>
                    <SectionTitle text="American Express" />
                  </Grid>
                  <Grid item xs={12} sm={autoFillMidAndNonQual || isCnpVCp || isErr ? 6 : 4}>
                    <TextField
                      name="rateSet.qualifiedDiscountRateAmex"
                      label="Qualified Rate"
                      required={isPropsRequired(
                        PageFields.qualifiedDiscountRateAmex,
                        watchApplicationType
                      )}
                      type="number"
                      disabled={readonly}
                      errors={errors}
                      control={control}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                    />
                  </Grid>
                  {!autoFillMidAndNonQual && (
                    <Fragment>
                      {!isCnpVCp && !isErr && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name="rateSet.midQualifiedDiscountRateAmex"
                            label="Mid-Qualified Rate"
                            required={isPropsRequired(
                              PageFields.midQualifiedDiscountRateAmex,
                              watchApplicationType
                            )}
                            type="number"
                            disabled={readonly}
                            errors={errors}
                            control={control}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={isCnpVCp || isErr ? 6 : 4}>
                        <TextField
                          name="rateSet.nonQualifiedDiscountRateAmex"
                          label={isErr ? 'Non-Qualified Surcharge' : 'Non-Qualified Rate'}
                          required={isPropsRequired(
                            PageFields.nonQualifiedDiscountRateAmex,
                            watchApplicationType
                          )}
                          type="number"
                          disabled={readonly}
                          errors={errors}
                          control={control}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Fragment>
                  )}
                  <Grid item xs={12} sm={autoFillMidAndNonQual || isCnpVCp || isErr ? 6 : 4}>
                    <TextField
                      name="rateSet.qualifiedTransactionFeeAmex"
                      label="Qualified Transaction Fee"
                      required={isPropsRequired(
                        PageFields.qualifiedTransactionFeeAmex,
                        watchApplicationType
                      )}
                      type="number"
                      disabled={readonly}
                      errors={errors}
                      control={control}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                  {!autoFillMidAndNonQual && (
                    <Fragment>
                      {!isErr && !isCnpVCp && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name="rateSet.midQualifiedTransactionFeeAmex"
                            label="Mid-Qualified Transaction Fee"
                            required={isPropsRequired(
                              PageFields.midQualifiedTransactionFeeAmex,
                              watchApplicationType
                            )}
                            type="number"
                            disabled={readonly}
                            errors={errors}
                            control={control}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </Grid>
                      )}
                      {!isErr && (
                        <Grid item xs={12} sm={isCnpVCp ? 6 : 4}>
                          <TextField
                            name="rateSet.nonQualifiedTransactionFeeAmex"
                            label="Non-Qualified Transaction Fee"
                            required={isPropsRequired(
                              PageFields.nonQualifiedTransactionFeeAmex,
                              watchApplicationType
                            )}
                            type="number"
                            disabled={readonly}
                            errors={errors}
                            control={control}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </Grid>
                      )}
                    </Fragment>
                  )}
                  {showDebitRates && (
                    <Grid item xs={12}>
                      <SectionTitle text="Debit" />
                    </Grid>
                  )}
                  {!isCnpVCp && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="rateSet.qualifiedDebitNonPINRate"
                        label="Signature Debit Non-Pin Based Rate"
                        required
                        type="number"
                        disabled={readonly}
                        errors={errors}
                        control={control}
                        defaultValue="0"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    </Grid>
                  )}
                  {!isCnpVCp && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="rateSet.qualifiedDebitNonPINFee"
                        label="Signature Debit Non-Pin Based Transaction Fee"
                        required
                        type="number"
                        disabled={readonly}
                        errors={errors}
                        control={control}
                        defaultValue="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  )}

                  {watchProcessingType === ProcessingTypes.cardPresent && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="rateSet.qualifiedDebitPINRate"
                          label="Debit Pin Based Rate"
                          required
                          type="number"
                          disabled={readonly}
                          errors={errors}
                          control={control}
                          defaultValue="0"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="rateSet.qualifiedDebitPINFee"
                          label="Debit Pin Based Transaction Fee"
                          required
                          type="number"
                          errors={errors}
                          control={control}
                          defaultValue="0"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  {showDebitRates && (
                    <Grid item xs={12}>
                      <SectionTitle text="EBT" />
                    </Grid>
                  )}
                  {showDebitRates && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="rateSet.ebtDiscountRate"
                        label="EBT Discount Rate"
                        required
                        type="number"
                        disabled={readonly}
                        errors={errors}
                        control={control}
                        defaultValue="0"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                      />
                    </Grid>
                  )}
                  {showDebitRates && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="rateSet.ebtTransactionFee"
                        label="EBT Transaction Fee"
                        required
                        type="number"
                        disabled={readonly}
                        errors={errors}
                        control={control}
                        defaultValue="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                  )}
                </Fragment>
              )}
            <Grid item xs={12}>
              <SectionTitleSwitch
                text="Other Volume & Other Item Fee (All Card Types)"
                isChecked={otherVolumeAndItemFeeSection}
                handleSwitchChange={value => setOtherVolumeAndItemFeeSection(value)}
              />
            </Grid>
            {otherVolumeAndItemFeeSection && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="rateSet.otherVolumeRate"
                    label="Discount Rate"
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="rateSet.otherItemFee"
                    label="Transaction Fee"
                    type="number"
                    disabled={readonly}
                    errors={errors}
                    control={control}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <SectionTitle text="Miscellaneous Fees" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.accountOnFileFee"
                label="Account on File Fee"
                required
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.chargebackFee"
                label="Chargeback Fee"
                required
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.retrievalFee"
                label="Retrieval Fee"
                required
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.monthlyMinimumFee"
                label="Monthly Minimum Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.avsFee"
                label="AVS Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.batchFee"
                label="Batch Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.debitAccessFee"
                label="Debit Access Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                name="rateSet.additionalServicesFee"
                label="Additional Service Fees"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Select
                name="rateSet.pciFee"
                label="PCI Fee"
                disabled={readonly}
                errors={errors}
                control={control}
                displayValue={true}
                defaultValue={PCIFee.waived}
                onChangeSuccess={handlePCIFeeChange}
                compareFn={(a, b) => {
                  return pciFeeTypes.indexOf(a[0]) - pciFeeTypes.indexOf(b[0]);
                }}
                options={PCIFeeTypes}
              />
            </Grid>
            {displayPCIFeeValue && (
              <Grid item xs={12} sm={3}>
                <TextField
                  name="rateSet.pciFeeValue"
                  label="PCI Fee Value"
                  type="number"
                  disabled={readonly}
                  errors={errors}
                  control={control}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={displayPCIFeeValue ? 2 : 3}>
              <TextField
                name="rateSet.earlyDeconversionFee"
                label="Early Deconversion Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={displayPCIFeeValue ? 2 : 3}>
              <TextField
                name="rateSet.annualFee"
                label="Annual Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={displayPCIFeeValue ? 2 : 3}>
              <TextField
                name="rateSet.regulatoryFee"
                label="Regulatory Fee"
                type="number"
                disabled={readonly}
                errors={errors}
                control={control}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <ErrorBoundary>
              {equipments.length > 0 && (
                <ProgramEquipment
                  equipmentData={template?.equipment}
                  group={group}
                  control={control}
                  errors={errors}
                  equipmentList={equipments}
                  setValue={setValue}
                  readonly={readonly}
                  watch={watch}
                  watchApplicationType={watchApplicationType}
                  ratePrograms={ratePrograms}
                />
              )}
            </ErrorBoundary>
            {!readonly && (
              <Grid item xs={12}>
                <SectionTitleSwitch
                  text="Enable share with agents"
                  isChecked={shareSection}
                  handleSwitchChange={value => {
                    setValue('shareWithAgentsEnabled', value, {shouldDirty: true});
                    if (!value) {
                      setValue('shareWithAgents', []);
                      setValue('shareWithAgentsIds', []);
                    }
                    setShareSection(value);
                  }}
                />
              </Grid>
            )}
            {!readonly && shareSection && (
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  multiple
                  getOptionLabel={getAgentLabel}
                  options={agentsList ?? []}
                  sx={{marginTop: 2}}
                  value={
                    agentsList
                      ? agentsList.filter(a => !!(shareWithAgents ?? []).find(x => x.id === a.id))
                      : []
                  }
                  renderInput={params => <MuiTextField {...params} label="Agents" />}
                  renderOption={(props, agent: AgentInfo, {selected}) => (
                    <MenuItem {...props} key={agent.id}>
                      <Checkbox checked={selected} />
                      <ListItemText primary={getAgentLabel(agent)} />
                    </MenuItem>
                  )}
                  onChange={onSelectAgentsChange}
                  disabled={!shareSection}
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
        <Divider />
        {!readonly && (
          <AccordionActions>
            <Button onClick={handleResetClick} disabled={!isDirty || saving}>
              Reset
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit(onSuccess, onError)}
              disabled={!isDirty || saving}
            >
              {saving && <CircularProgress sx={styles.buttonProgress} size={24} />}Save
            </Button>
          </AccordionActions>
        )}
      </Accordion>
    </FormProvider>
  );
};
